import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Edit, useRefresh, useNotify } from 'react-admin'
import { Typography } from 'antd'
import apolloClient from 'clients/apollo'
import Chip from '@mui/material/Chip'
import { ACCOUNT_FORMS_SUBSCRIPTION } from 'shared/gql'
import AppState from 'shared/app_state';

import { EntryList } from '../entry/list'
import { FormIntegrations } from 'formtopia/react-admin/resources/form/integrations'
import { FormSettings } from './settings'
import { NotificationEditor, PowCloudRAFormTab, PowCloudRATabbedForm } from 'shared/react-admin'
import { FormDesignTab } from './design'
import { gql, useSubscription } from '@apollo/client'

const { Title } = Typography

export const ACCOUNT_FORM_ENTRIES_COUNT = gql`
  query _allEntriesMeta($filter: EntryFilter) {
    _allEntriesMeta(filter: $filter) {
      count
    }
  }
`

export interface FormEditAsideProps {
  record?: any
}

type FormEditProps = {
  editLayoutWrapper?: {
    component: any
    props: { [key: string]: any }
  }
  id?: string
  history?: any
}

const TabContentWithHeader = ({ content, ...rest }) => {
  const { component: Component } = content

  return (
    <div style={{ width: '100%', padding: 10 }}>
      <Title level={4}>{content.header}</Title>
      <div style={{ width: '85%' }}>
        {content.description}
      </div>
      <div style={{ paddingTop: 10 }}>
        <Component {...rest} />
      </div>
    </div>
  )
}

export const FormEdit: FC<FormEditProps> = ({ editLayoutWrapper = { component: null, props: null } }) => {
  const [entryCount, setEntryCount] = useState(0)
  const params = useParams()
  // const [initialized, setInitialized] = useState<boolean>(false)

  // const init = () => {
  //   // TODO
  //   // EDIT consists of 4 tabs (design, entries, notifications, settings)
  //   // Problem:
  //   //    Resource.edit sets edit route to resource/:id. resource/:id does not display any of the
  //   //    four tabs b/c tabs are route based (i.e. form/:id/design). so vanilla redirect="edit" does
  //   //    not display the desired initial view (form/:id/design).
  //   // Possible Solutions:
  //   //    1) set default tab on Edit, TabbedForm, or FormTab components (does not seem possible)
  //   //    2) overwrite the router path assigned to the edit component in the Resource component. (does
  //   //    not seem possible https://stackoverflow.com/questions/54585643/custom-path-for-resource-route-in-react-admin)
  //   //    3) HACK trigger redirect within Edit component on mount (method used below)

  //   if (location.pathname.endsWith(record.id as string)) {
  //     // redirect to default form tab path - design
  //     redirect(`${location.pathname}/entries`)
  //   }
  //   setInitialized(true)
  // }

  useSubscription(ACCOUNT_FORMS_SUBSCRIPTION, {
    variables: { accountId: AppState.getCurrentAccount().id },
    onSubscriptionData: (data) => {
      const { subscriptionData: { data: { accountForms } } } = data
      if (accountForms?.payload?.form_id != params.id) return
      if (['form_entry_create', 'form_entry_delete'].includes(accountForms.payload.event)) fetchEntryCount()
    }
  })

  const fetchEntryCount = async () => {
    try {
      const {
        data: { _allEntriesMeta: { count } }
      } = await apolloClient.query({
        query: ACCOUNT_FORM_ENTRIES_COUNT,
        variables: {
          filter: {
            formId: params.id
          }
        },
        fetchPolicy: 'no-cache'
      })

      setEntryCount(count)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    fetchEntryCount()
  }, [])

  const tabs = [
    {
      tab: {
        label: 'Entries',
        path: 'entries',
      },
      content: {
        header: 'Form Entries',
        description: 'Responses to your form will be displayed here as they are received.',
        component: EntryList
      }
    },
    {
      tab: {
        label: 'Design',
        path: 'design',
      },
      content: {
        header: 'Form Designer',
        description: "Enter the names of each field you want to collect in the editor on the left. You'll see a real-time preview of your form on the right.",
        component: FormDesignTab
      }
    },
    {
      tab: {
        label: 'Publish',
        path: 'publish',
      },
      content: {
        header: 'Publish',
        description: "Your form has it's own dedicated page on powcloud.com. You can share your form link, or embed the form directly into your existing website.",
        component: FormIntegrations
      }
    },
    {
      tab: {
        label: 'Workflow',
        path: 'workflow',
      },
      content: {
        header: 'Workflow',
        description: 'Workflow allows you to stream data from your form to other services. When an event occurs, form data will be sent to the selected channels.',
        component: NotificationEditor
      }
    },
    {
      tab: {
        label: 'Settings',
        path: 'settings',
      },
      content: {
        header: 'Settings',
        description: 'Customize how your form is displayed and advanced options.',
        component: FormSettings
      }
    }
  ]

  const renderForm = () => {
    return (
      <PowCloudRATabbedForm variant="outlined" toolbar={false}>
        {tabs.map(({ tab, content }, i) => {
          const label = tab.path === 'entries' ? entryCount > 1 ? <span style={{ display: 'flex', alignItems: 'center' }}>{tab.label} <div style={{ marginLeft: 10 }}><Chip label={entryCount.toString()} /></div></span> : tab.label : tab.label

          return (
            <PowCloudRAFormTab key={i} label={label} path={tab.path}>
              <TabContentWithHeader content={content} />
            </PowCloudRAFormTab>
          )
        })}
      </PowCloudRATabbedForm >
    )
  }

  // console.log(data, error, loading)

  // useEffect(() => {
  //   if (record) init()
  // }, [record])

  // if (!initialized) return null
  // console.log('record', record)

  // https://marmelab.com/react-admin/CreateEdit.html#undoable
  // *** NOTE undoable (and it's optimistic rendering) appears to cause visual bugs
  // and loading issues when combined with react-admin TabbedForm's rendering
  // strategy (see PowCloudRATabbedForm for more notes). Turn off undoable

  const { component: WrapperComponent, props: wrapperProps } = editLayoutWrapper

  // const notify = useNotify()

  return (
    <Edit mutationMode="pessimistic" redirect={(resource, id) => `${resource}/${id}/settings`}>
      {!!WrapperComponent ? <WrapperComponent {...wrapperProps}>{renderForm()}</WrapperComponent> : <>{renderForm()}</>}
      {/* <TabbedForm>
        <FormTab label="Entries" path="entries">
          <EntryList />
        </FormTab>
      </TabbedForm> */}
    </Edit >
  )
}
